import { Injectable } from '@angular/core';
import { BaseAPI } from '@core/services/base-api.service';
import { Mapper } from '@core/services/base-mapper.service';
import { APIConstants } from '@shared/constants';
import { filterNullEntity, FunctionUtil } from '@shared/utils';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Country } from '@shared-features/models/country.model';
import { Fleet } from '@features/fleets/models/fleet.model';
import { Truck } from '@features/fleets/models/truck.model';
import { InsuranceModel } from '@features/fleets/models/insurance-model.model';
import { Customer } from '../../customers/models/customer.model';
import { DriversType } from '../enums/fleets.enum';
import { Driver } from '@features/fleets/models/driver.model';
import { PermittedDriversAndTrucks } from '../../fleets/models/permitted-drivers-and-trucks.model';
import { Shipment } from '@features/shared-features/models/shipment.model';
import { IRunningShipmentPayload } from '@shared-features/interfaces';
import { ShipmentDeliveryNote } from '@features/invoices/models/shipment-delivery-note.model';
import { SelectedCountry } from '@shared-features/enums';
@Injectable({
  providedIn: 'root',
})
export class SharedAPI {
  constructor(private baseAPI: BaseAPI, private mapper: Mapper) {}

  getAllCountries(): Observable<Country[]> {
    const allowedIsoCodes = Object.values(SelectedCountry) as string[];
    return this.baseAPI.get(APIConstants.API_GET_ALL_COUNTRIES).pipe(
      map((res) => {
        const countries = this.mapper.fromJson(Country, res.data) as Country[];
        return countries.filter((country) => allowedIsoCodes.includes(country.isoCode?.toLowerCase()));
      })
    );
  }

  getAllActiveFleets(): Observable<Fleet[]> {
    return this.baseAPI.get(APIConstants.API_GET_ACTIVE_FLEETS).pipe(
      map((res) => {
        return this.mapper.fromJson(Fleet, res.data);
      })
    );
  }

  getAllActiveFreelancers(): Observable<Fleet[]> {
    return this.baseAPI.get(APIConstants.API_GET_ACTIVE_FLEETS).pipe(
      map((res) => {
        return this.mapper.fromJson(Fleet, res.data);
      })
    );
  }

  getAllInsuranceCompanies(): Observable<InsuranceModel[]> {
    return this.baseAPI.get(APIConstants.API_GET_ALL_INSURANCE_COMPANIES).pipe(
      map((res) => {
        return this.mapper.fromJson(InsuranceModel, res.data);
      })
    );
  }

  getAllInsuranceCovers(): Observable<InsuranceModel[]> {
    return this.baseAPI.get(APIConstants.API_GET_ALL_INSURANCE_COVERS).pipe(
      map((res) => {
        return this.mapper.fromJson(InsuranceModel, res.data);
      })
    );
  }

  getAllTrucks(): Observable<Truck[]> {
    return this.baseAPI.get(APIConstants.API_GET_ALL_TRUCKS).pipe(
      map((res) => {
        return this.mapper.fromJson(Truck, res.data);
      })
    );
  }

  getFleetActiveTrucks(fleetId): Observable<Truck[]> {
    return this.baseAPI.get(APIConstants.API_GET_FLEET_ACTIVE_TRUCKS, { params: filterNullEntity({ fleetId }) }).pipe(
      map((res) => {
        return this.mapper.fromJson(Truck, res.data);
      })
    );
  }

  getAllCustomers(searchTerm?: any): Observable<Customer[]> {
    const params = filterNullEntity({
      searchTerm,
    });
    return this.baseAPI.get(APIConstants.API_GET_ALL_CUSTOMERS, { params }).pipe(
      map((res) => {
        return this.mapper.fromJson(Customer, res.data);
      })
    );
  }

  getActiveDrivers(driverType?: DriversType, fleetId?: number, searchTerm?: string): Observable<Driver[]> {
    const params = filterNullEntity({
      driverType,
      fleetId,
      searchTerm,
      platform: 'WEB',
    });
    return this.baseAPI.get(APIConstants.API_GET_ACTIVE_DRIVERS, { params }).pipe(
      map((res) => {
        return this.mapper.fromJson(Driver, res.data);
      })
    );
  }

  getPermitAccess(receiverId: number): Observable<PermittedDriversAndTrucks> {
    return this.baseAPI.get(`${APIConstants.API_GET_PERMIT_ACCESS}/${receiverId}`).pipe(
      map((res) => {
        return this.mapper.fromJson(PermittedDriversAndTrucks, res.data);
      })
    );
  }

  getRunningShipments(payload: IRunningShipmentPayload): Observable<Shipment> {
    const { driverId, truckId, type } = payload;

    return this.baseAPI
      .get(APIConstants.GET_RUNNING_SHIPMENTS + (driverId ? driverId : truckId), {
        params: { type },
      })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(Shipment, res.data.shipment);
        })
      );
  }

  getShipmentById(shipmentId: string | number): Observable<Shipment> {
    return forkJoin(
      [
        APIConstants.GET_SHIPMENT_SUMMARY_BY_ID(shipmentId),
        APIConstants.GET_SHIPMENT_TRACKING_BY_ID(shipmentId),
        APIConstants.GET_SHIPMENT_REQUESTS_BY_ID(shipmentId),
        APIConstants.GET_SHIPMENT_FINANCIAL_BY_ID(shipmentId),
      ]
        .map((url) => this.baseAPI.get(url).pipe(map((res) => this.mapper.fromJson(Shipment, res.data))))
        .concat([
          this.baseAPI.get(APIConstants.GET_DELIVERY_NOTE, { params: { shipmentId } }).pipe(
            map((res) => ({ deliveryNote: this.mapper.fromJson(ShipmentDeliveryNote, res.data) })),
            catchError(() => of({ deliveryNote: [] }))
          ),
        ])
    ).pipe(map((res) => FunctionUtil.mergeIgnoringNulls<Shipment>(...res)));
  }
}
